import {JwtContext, signOut} from '@/providers/JWTProvider';
import {Alert, CircularProgress, Container, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';
import {useCallback, useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

export type HandleSubmitResponse = {
    message: string;
    errorCode: number;
};

const targetAlertIcon = '& .MuiAlert-icon';
const targetAlertMessage = '& .MuiAlert-message';

const QrAction: React.FC = () => {
    const {actionItem, activityId} = useParams();
    const user = useContext(JwtContext);
    const [responseMessage, setResponseMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [doFetchQrAction, setDoFetchQrAction] = useState(false);

    let displayName = '';
    let accessToken = '';

    if (user) {
        displayName = user.displayName;
        accessToken = user.accessToken;
    }

    const performQrAction = useCallback(async () => {
        const url = new URL('/student/perform-qr-action', import.meta.env.VITE_APP_API_ENDPOINT);

        setErrorMessage('');
        setResponseMessage('');

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                action: actionItem,
                activityId: activityId,
            }),
        });

        if (response.ok) {
            const json = (await response.json()) as HandleSubmitResponse;

            if (json.errorCode !== 0) {
                setErrorMessage(json.message);
                return;
            }

            setResponseMessage(json.message);
            return;
        }

        if (response.status === 401) {
            setErrorMessage('Invalid Credentials Error');
            signOut();
            window.location.reload();
            return;
        }

        setErrorMessage('An unknown error has occurred.');
    }, [accessToken, actionItem, activityId]);

    useEffect(() => {
        if (!doFetchQrAction) {
            setDoFetchQrAction(true);
        }
    }, [doFetchQrAction]);

    useEffect(() => {
        if (doFetchQrAction) {
            void performQrAction();
        }
    }, [doFetchQrAction, performQrAction]);

    return (
        <Container maxWidth='sm' sx={{mt: 3, backgroundColor: '#fff', borderRadius: '4px', p: 2}}>
            <Typography>Welcome, {displayName}</Typography>
            <Stack direction='column' justifyContent='center' sx={{mt: 2}}>
                {errorMessage && (
                    <Alert
                        severity='error'
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            [targetAlertIcon]: {
                                fontSize: 120,
                            },
                            [targetAlertMessage]: {
                                fontSize: '1.5em',
                            },
                        }}
                    >
                        {errorMessage}
                    </Alert>
                )}
                {responseMessage && (
                    <Alert
                        severity='success'
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            [targetAlertIcon]: {
                                fontSize: 120,
                            },
                            [targetAlertMessage]: {
                                fontSize: '1.5em',
                            },
                        }}
                    >
                        {responseMessage}
                    </Alert>
                )}
                {!(responseMessage || errorMessage) && <CircularProgress />}
            </Stack>
        </Container>
    );
};

export default QrAction;
